import React, { useState, useRef, useEffect } from 'react'

import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
} from 'react-image-crop'
import { canvasPreview } from './canvasPreview'
import { useDebounceEffect } from './useDebounceEffect'

import 'react-image-crop/dist/ReactCrop.css'
import { FstyrButton } from '../../FstyrComponents/FstyrButton'



type props = {
  onCut: (value: string) => void
  showComponent: boolean
  setShowComponent: (value: boolean) => void
}

const ImageCrop = (props: props) => {
  const [imgSrc, setImgSrc] = useState('')
  const previewCanvasRef = useRef<HTMLCanvasElement>(null)
  const imgRef = useRef<HTMLImageElement>(null)
  const [crop, setCrop] = useState<Crop>()
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>()
  const [scale, setScale] = useState(1)
  const [aspect, setAspect] = useState<number>(7 / 9)
  const [fileName, setFileName] = useState<string>("")

  const [cropSubmitted, setCropSubmitted] = useState<boolean>(false)

  const fileInputRef = useRef<HTMLInputElement>(null);

  function centerAspectCrop() {
    var mediaHeight = 200
    var aspect = 7 / 9
    return centerCrop(
      makeAspectCrop(
        {
          unit: 'px',
          height: mediaHeight,
          width: mediaHeight * aspect
        },
        aspect,
        mediaHeight * aspect,
        mediaHeight,
      ),
      mediaHeight * aspect,
      mediaHeight,
    )
  }

  function rotate(degrees: number, callback) {
    var canvas = document.createElement('canvas')
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error('No 2d context')
    }

    var image = new Image();

    image.onload = function () {
      canvas.width = image.height;
      canvas.height = image.width;

      ctx.rotate(degrees * Math.PI / 180);
      if (degrees === 90) {
        ctx.translate(0, -canvas.width);
      } else if (degrees === 270) {
        ctx.translate(-canvas.height, 0);
      }
      ctx.drawImage(image, 0, 0);
      callback(canvas.toDataURL("image/jpeg"))
    };

    image.src = imgSrc;

  }

  const rotateImage = (deg: number) => {
    setCrop(undefined) // Makes crop preview update between images.
    rotate(deg, function (res) { setImgSrc(res) })
  }

  function onSelectFile(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined) // Makes crop preview update between images.
      const reader = new FileReader()
      reader.addEventListener('load', () =>
        setImgSrc(reader.result?.toString() || ''),
      )
      reader.readAsDataURL(e.target.files[0])
      setFileName(e.target.files[0].name);
    }
  }

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    setCrop(centerAspectCrop())
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
        )
      }
    },
    100,
    [completedCrop, scale],
  )

  const resetState = () => {
    setImgSrc('')
    setScale(1)
    setAspect(7 / 9)
    setFileName("")
    setCrop(undefined)
    setCompletedCrop(undefined)
    setCropSubmitted(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
    props.onCut("");
  }

  useEffect(() => {
    if (!props.showComponent) {
      resetState();
    }
  }, [props.showComponent]
  )

  const handleCropSubmit = (submitted: boolean) => {
    if (submitted) {
      //BASE64 STRING THAT SHOULD BE SAVED IN DB
      let base64 = previewCanvasRef.current?.toDataURL("image/jpeg").split(';base64,')[1] ?? ""
      props.onCut(base64);
      setCropSubmitted(true);
    }
    else {
      setCropSubmitted(false);
    }
  }

  const onButtonClick = () => {
    props.setShowComponent(true);
    // `current` points to the mounted file input element
    fileInputRef.current?.click();
    setCropSubmitted(false);
  };

  return (
    <div>
      <div>
        <input type="file" accept=".jpg" ref={fileInputRef} style={{ display: 'none' }} onChange={onSelectFile} />
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <div role="button" className="button button-primary" onClick={onButtonClick} >Vælg billede</div>
          <p style={{ maxWidth: 300, marginLeft: 20, whiteSpace: 'nowrap' }}>{fileName}</p>
        </div>
      </div>
      {props.showComponent && Boolean(imgSrc) && (
        <div>
          <div style={{ display: 'flex', flexDirection: "row" }}>
            {Boolean(imgSrc) && (
              <ReactCrop
                crop={crop}
                onChange={(PixelCrop, _) => setCrop(PixelCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                style={{ pointerEvents: cropSubmitted ? 'none' : 'auto', filter: cropSubmitted ? "brightness(0.7)" : "brightness(1)" }}
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imgSrc}
                  style={{ transform: `scale(${scale})` }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            )}
            <canvas
              ref={previewCanvasRef}
              style={{
                border: '1px solid black',
                objectFit: 'contain',
                height: 400,
                width: 400 * (7 / 9)
              }}
            />
          </div>
          <div style={{ marginTop: 10 }}>
            {cropSubmitted ?
              <div className='fstyr-left-button-group'>
                <FstyrButton label="Beskær igen" onClick={() => handleCropSubmit(false)} type={"secondary"} />
              </div>
              :
              <div className='fstyr-left-button-group'>
                <FstyrButton label="Beskær" onClick={() => handleCropSubmit(true)} />
                <label style={{ marginLeft: "10px" }} className="form-label" htmlFor="rotate-input">Roter billede:</label>
                <FstyrButton label="-90°" onClick={() => rotateImage(270)} type={"secondary"} />
                <FstyrButton label="+90°" onClick={() => rotateImage(90)} type={"secondary"} />
              </div>
            }
          </div>
        </div>
      )}

    </div>
  )
}

export default ImageCrop;
